import { ALMOND_PRODUCT_ID } from "./almondPublicationCatalogV1.mjs";

// A presentation catalogue only. It never creates products, editions, volumes,
// payment entitlements, or publications. The actual bindings remain server-owned.
export const EDITORIAL_WORKSPACES = Object.freeze([
  { key: "open-library", title: "Открытая библиотека", description: "Рецепты, статьи, истории и технологии", group: "library", productSlugs: [] },
  { key: "almond", title: "Том I «Миндаль»", description: "Исследования, главы и рецептуры", group: "programs", productSlugs: ["almond"] },
  { key: "medovik", title: "Медовик как бизнес", description: "Материалы авторской программы", group: "programs", productSlugs: ["medovik"] },
  { key: "bento", title: "Бенто-торты от А до Я", description: "Уроки, рецептуры и практика", group: "programs", productSlugs: ["bento"] },
  { key: "lineart", title: "Контурные рисунки на бенто", description: "Материалы программы", group: "programs", productSlugs: ["lineart"] },
  { key: "gingerbread", title: "Пряничная мастерская", description: "Технологии и творческие материалы", group: "workshops", productSlugs: ["gingerbread", "gingerbread-workshop"] },
  { key: "media-studio", title: "Медиа-студия", description: "Материалы и инструменты для контента", group: "workshops", productSlugs: ["media-studio"] },
]);

export const OPEN_LIBRARY_EDITORIAL_SECTIONS = Object.freeze([
  { id: "RECIPES", title: "Рецепты" },
  { id: "QUESTIONS", title: "Ответы на вопросы" },
  { id: "TECHNOLOGY", title: "Технологии" },
  { id: "TROUBLESHOOTING", title: "Ошибки и решения" },
  { id: "INGREDIENTS", title: "Ингредиенты" },
  { id: "HISTORY", title: "Истории и традиции" },
]);

const simplified = (value) => String(value || "").trim().toLocaleLowerCase("ru-RU")
  .replace(/[«»]/g, "").replace(/\s+/g, " ");

const EXPECTED_PRODUCT_TITLES = {
  almond: ["Том I «Миндаль»"],
  medovik: ["Медовик как бизнес"],
  bento: ["Бенто-торты от А до Я"],
  lineart: ["Контурные рисунки на бенто"],
  gingerbread: ["Пряничная мастерская"],
  "media-studio": ["Медиа-студия"],
};

export function isTechnicalEditorialEdition(edition) {
  const label = String(edition?.title || "") + " " + String(edition?.slug || "");
  return /(?:^|\s|[-_])(QA|E2E)(?:\s|[-_]|$)|runtime|publication mirror/i.test(label);
}

export function productForEditorialWorkspace(workspaceKey, products = []) {
  if (workspaceKey === "open-library") return null;
  const space = EDITORIAL_WORKSPACES.find((entry) => entry.key === workspaceKey);
  if (!space) return null;
  if (workspaceKey === "almond") {
    const canonical = products.find((p) => p.id === ALMOND_PRODUCT_ID);
    if (canonical) return canonical;
  }
  return products.find((p) =>
    space.productSlugs.includes(String(p.slug || "").toLowerCase()) ||
    (EXPECTED_PRODUCT_TITLES[workspaceKey] || []).some((label) => simplified(label) === simplified(p.title))
  ) || null;
}

export function editorialVolumesForWorkspace(workspaceKey, volumes = [], products = []) {
  if (workspaceKey === "open-library") {
    return volumes.filter((v) => !v.product_id &&
      (["open-library", "open-library-author-intro"].includes(v.slug) ||
       simplified(v.title) === simplified("Открытая библиотека")));
  }
  const product = productForEditorialWorkspace(workspaceKey, products);
  if (!product) return [];
  return volumes.filter((v) => v.product_id === product.id);
}

export function isSafeEditorialCreationTarget(workspaceKey, volume, editions = [], products = []) {
  if (!volume) return false;
  const edition = editions.find((e) => e.id === volume.edition_id);
  // A QA-backed volume may hold historical authored drafts but must NEVER be
  // silently used as a destination for a new customer-facing publication.
  if (!edition || isTechnicalEditorialEdition(edition)) return false;
  if (workspaceKey === "open-library") {
    return !volume.product_id && volume.slug === "open-library";
  }
  const product = productForEditorialWorkspace(workspaceKey, products);
  return !!product && volume.product_id === product.id;
}

const PROTECTED_EDITORIAL_FIXTURES = new Set([
  "0f26cb7d-f6ce-484c-b9a0-596b33e60419",
  "9c60f83f-b5ab-438b-b27e-09f41480a620",
]);

export function canOfferEditorialDraftDelete(material, role) {
  // UI is only a convenience. The backend independently checks scope,
  // publish references, approved snapshots, protected blocks and history.
  return ["owner", "administrator"].includes(role)
    && material?.status === "draft"
    && !material?.qa_fixture
    && !material?.e2e_fixture
    && !material?.published_snapshot_id
    && !material?.owner_lifecycle_operation
    && !material?.open_library_last_action
    && !(material?.owner_lifecycle_revision > 0)
    && !PROTECTED_EDITORIAL_FIXTURES.has(material?.id);
}
